/**
 * WorkflowEditorBridge — postMessage bridge between the workflow editor
 * iframe (served by app_runner) and the host SchedulesLive.
 *
 * Protocol v:1 (see apps/workflow_editor/src/types.ts):
 *
 *   Editor → LiveView (inbound to this hook):
 *     {v:1, type:"ready"}
 *     {v:1, type:"graph_changed", graph, dirty}
 *     {v:1, type:"node_selected", node_id}
 *     {v:1, type:"save", graph}
 *
 *   LiveView → Editor (outbound from this hook):
 *     {v:1, type:"load_graph", graph, graph_status, editable}
 *     {v:1, type:"execution_step", step_id, status, output?, error?}
 *     {v:1, type:"execution_reset"}
 *     {v:1, type:"available_resources", resources, strings}
 *     {v:1, type:"save_ack", version}
 *     {v:1, type:"save_error", reason}
 *
 * Security:
 *   * Inbound `event.origin` is validated against the iframe's data-origin
 *     attribute. Invalid origins are logged and dropped.
 *   * Outbound target origin mirrors the same attribute.
 *
 * The element must be an <iframe data-origin="<app_runner_origin>"> with
 * `phx-hook="WorkflowEditorBridge"`. LiveView sends pushes via:
 *
 *   push_event(socket, "workflow_editor:push", %{type: "...", ...})
 */
import { t } from "../i18n.js"

const WorkflowEditorBridge = {
  mounted() {
    this.targetOrigin = this.el.dataset.origin || "*";
    this.editorReady = false;
    this.pendingPushes = [];
    this.onMessage = (event) => this.handleInbound(event);
    window.addEventListener("message", this.onMessage);

    // Branding/theme inheritance: mirror the host's resolved CSS vars into the
    // iframe so the editor reflects partnership/workspace branding + dark mode.
    // The parent <html> already has branding resolved (PR5 brand_style_block),
    // so getComputedStyle is the source of truth — nothing serialized server-side.
    this.themeObserver = new MutationObserver(() => {
      if (this.editorReady) this.postStyleVars();
    });
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    // LiveView → editor pushes.
    this.handleEvent("workflow_editor:push", (payload) => {
      // Workflow uplift Fase 0.1: if the iframe hasn't signaled "ready"
      // yet, buffer the push and flush on ready. Previously, a
      // load_graph push that arrived during iframe boot (very common
      // for drawer-triggered edits on freshly opened editors) was sent
      // via postMessage to an iframe whose React listener wasn't
      // mounted yet — silently dropped.
      if (!this.editorReady && this.isReplayable(payload)) {
        this.pendingPushes.push(payload);
        return;
      }
      this.postToEditor(payload);
    });
  },

  destroyed() {
    window.removeEventListener("message", this.onMessage);
    this.themeObserver?.disconnect();
  },

  updated() {
    // Allow the origin attribute to be refreshed without a full remount.
    const newOrigin = this.el.dataset.origin;
    if (newOrigin) this.targetOrigin = newOrigin;
  },

  handleInbound(event) {
    if (!this.isEditorWindow(event.source)) return;
    if (!this.isOriginAllowed(event.origin)) {
      console.warn(
        "[WorkflowEditorBridge] rejected message from unexpected origin:",
        event.origin
      );
      return;
    }
    const msg = event.data;
    if (!msg || msg.v !== 1 || typeof msg.type !== "string") return;

    switch (msg.type) {
      case "ready":
        this.editorReady = true;
        this.pushEvent("workflow_editor:ready", {});
        // Flush any pushes that arrived while the iframe was booting.
        // Keep order; LiveView's load_graph wins by being most recent
        // (postMessage handlers in the editor app dedupe on graph_version).
        const queued = this.pendingPushes.splice(0);
        for (const payload of queued) {
          this.postToEditor(payload);
        }
        // Push the current branding/theme once the editor can receive it.
        this.postStyleVars();
        break;
      case "graph_changed":
        this.pushEventDebounced(
          "workflow_editor:graph_changed",
          { graph: msg.graph, dirty: !!msg.dirty },
          1500
        );
        break;
      case "node_selected":
        this.pushEvent("workflow_editor:node_selected", {
          node_id: msg.node_id,
        });
        break;
      case "save":
        if (this.saveTimer) clearTimeout(this.saveTimer);
        this.pushEvent("workflow_editor:save", { graph: msg.graph });
        break;
      case "suggest_input_mapping":
        this.pushEvent("workflow_editor:suggest_input_mapping", {
          node_id: msg.node_id,
          graph: msg.graph,
        });
        break;
      case "request_node_dry_run":
        // Workflow uplift Fase 6.2 — per-node dry-run.
        this.pushEvent("workflow_editor:request_node_dry_run", {
          node_id: msg.node_id,
          input: msg.input,
        });
        break;
      case "request_open_tool_editor":
        // Pencil affordance on a `tool` node — SchedulesLive answers
        // with `push_navigate` to `/tools/:id/edit`.
        if (typeof msg.tool_id === "string" && msg.tool_id !== "") {
          this.pushEvent("workflow_editor:request_open_tool_editor", {
            tool_id: msg.tool_id,
          });
        }
        break;
    }
  },

  // The host branding tokens the editor's :root consumes. Read from the live
  // computed style (which reflects partnership/workspace branding + dark mode)
  // and post to the iframe. Unresolved aliases (some engines return `var(...)`)
  // are skipped so the editor keeps its own default.
  STYLE_VAR_NAMES: [
    "--accent-primary",
    "--accent-secondary",
    "--accent-primary-hover",
    "--accent-primary-active",
    "--bg-primary",
    "--bg-secondary",
    "--bg-card",
    "--bg-sidebar",
    "--text-primary",
    "--text-secondary",
    "--text-tertiary",
    "--text-on-primary",
    "--border-default",
    "--border-strong",
    "--button-primary-bg",
    "--button-primary-bg-active",
    "--button-primary-text",
    "--button-radius",
  ],

  postStyleVars() {
    const cs = getComputedStyle(document.documentElement);
    const vars = {};
    for (const name of this.STYLE_VAR_NAMES) {
      const value = cs.getPropertyValue(name).trim();
      if (value && !value.includes("var(")) vars[name] = value;
    }
    if (Object.keys(vars).length > 0) this.postToEditor({ type: "style_vars", vars });
  },

  postToEditor(payload) {
    const iframe = this.el;
    if (!iframe.contentWindow) return;
    const msg = { v: 1, ...payload };
    try {
      iframe.contentWindow.postMessage(msg, this.targetOrigin);
      // Phase 10.1 debug breadcrumb — when users reported "Matt edited
      // the workflow but the graph didn't refresh", we needed a quick
      // way to confirm the bridge actually pushed without spinning up
      // server tooling. Pair this with the React-side log so a DevTools
      // session shows both halves of the handshake.
      if (msg.type === "load_graph") {
        console.debug(
          "[WorkflowEditorBridge] → load_graph",
          msg.graph_version || t("(no version)")
        );
      }
    } catch (err) {
      console.error("[WorkflowEditorBridge] postMessage failed:", err);
    }
  },

  // Only state-altering pushes (load_graph, available_resources) are worth
  // replaying when the iframe boots late. Ephemeral pushes (save_ack /
  // save_error / execution_step / execution_reset) are tied to a moment
  // and would confuse the editor if replayed minutes later.
  isReplayable(payload) {
    return (
      payload && (payload.type === "load_graph" || payload.type === "available_resources")
    );
  },

  isEditorWindow(source) {
    return source && source === this.el.contentWindow;
  },

  isOriginAllowed(origin) {
    if (this.targetOrigin === "*") return true;
    return origin === this.targetOrigin;
  },

  pushEventDebounced(name, payload, ms) {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.pushEvent(name, payload);
      this.saveTimer = null;
    }, ms);
  },
};

export default WorkflowEditorBridge;
